
import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { RFPEstimate } from "@/types/project-types";
import { ComponentType } from "@/types/estimation-types";
import { componentTypes } from "@/data/estimationData";
import { updateEstimate, calculateTotals, exportToExcel } from "./rfp-utils";
import { sendEstimateEmail } from "./EstimationEmailService";
import RFPInfoForm from "./RFPInfoForm";
import EstimationSummary from "./EstimationSummary";
import EstimationTable from "./EstimationTable";
import EstimationNotes from "./EstimationNotes";
import EstimationActionButtons from "./EstimationActionButtons";

interface EstimationTabProps {
  rfpId: string | undefined;
  formData: {
    name: string;
    description: string;
  };
  componentEstimates: RFPEstimate[];
  onFormChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onSaveRFPInfo: () => Promise<void>;
  onUpdateEstimate: (newEstimates: RFPEstimate[]) => void;
  onSaveEstimation: () => Promise<void>;
}

const EstimationTab: React.FC<EstimationTabProps> = ({
  rfpId,
  formData,
  componentEstimates,
  onFormChange,
  onSaveRFPInfo,
  onUpdateEstimate,
  onSaveEstimation
}) => {
  const [isSaving, setIsSaving] = useState(false);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  
  const handleEstimateUpdate = (index: number, field: string, value: number) => {
    const newEstimates = updateEstimate(componentEstimates, index, field, value);
    onUpdateEstimate(newEstimates);
  };

  const handleSaveEstimation = async () => {
    setIsSaving(true);
    try {
      await onSaveEstimation();
    } finally {
      setIsSaving(false);
    }
  };

  const handleSendEmail = async () => {
    setIsSendingEmail(true);
    try {
      await sendEstimateEmail();
    } finally {
      setIsSendingEmail(false);
    }
  };
  
  const handleExportExcel = () => {
    exportToExcel(componentEstimates, formData.name, {
      projectName: formData.name,
      description: formData.description,
      rfpId: rfpId
    });
  };

  const totals = calculateTotals(componentEstimates);
  
  const sortedEstimates = [...componentEstimates].sort((a, b) => {
    const aIndex = componentTypes.indexOf(a.component_type as ComponentType);
    const bIndex = componentTypes.indexOf(b.component_type as ComponentType);
    return aIndex - bIndex;
  });

  return (
    <div className="space-y-4 pt-4">
      <RFPInfoForm 
        formData={formData} 
        onFormChange={onFormChange} 
        onSave={onSaveRFPInfo} 
      />

      <Card className="bg-white dark:bg-gray-900 border-purple-100 dark:border-gray-800">
        <CardHeader className="border-b border-purple-100 dark:border-gray-800">
          <CardTitle className="text-xl text-purple-700 dark:text-gray-300 flex justify-between items-center flex-wrap gap-2">
            <span>Component Estimates</span>
            <EstimationActionButtons
              isSaving={isSaving}
              isSendingEmail={isSendingEmail}
              onSave={handleSaveEstimation}
              onSendEmail={handleSendEmail}
              onExportExcel={handleExportExcel}
            />
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-6 overflow-x-auto">
          <EstimationSummary 
            totalComponents={totals.simple_count + totals.medium_count + totals.complex_count + totals.supercomplex_count} 
            devDays={totals.total_dev_days} 
            totalDays={totals.total_days} 
          />

          <EstimationTable 
            estimates={sortedEstimates} 
            totals={totals} 
            onUpdateEstimate={handleEstimateUpdate} 
          />
          
          <EstimationNotes />
        </CardContent>
      </Card>
    </div>
  );
};

export default EstimationTab;
